// Yıl bazlı vergi takvimi yardımcıları (web).
//
// NEDEN: Takvim önceden TAX_CALENDAR_2026'ya sabitlenmişti; 1 Ocak 2027'de site
// sessizce 2026 verisini "bu yılın takvimi" diye göstermeye devam edecekti.
// Artık yıl her zaman bugünün tarihinden (veya URL'den) gelir ve veri yoksa
// bu AÇIKÇA söylenir.
//
// Saat bağımlı her fonksiyon `now` parametresi alır (test edilebilsin diye).
// AYNI KURALLAR build script'lerinde de var: scripts/lib/tax-calendar.js —
// birini değiştirirsen ötekini de değiştir.
import { TAX_CALENDARS, TAX_CALENDAR_DURUM, MONTHS, TaxEvent } from "./tax-calendar";

export type TakvimDurumu = "resmi" | "tahmini";

/** Verisi (en az 1 kaydı) olan yıllar, artan sırada. Boş yıl burada yer almaz. */
export const VERI_OLAN_YILLAR: number[] = Object.keys(TAX_CALENDARS)
  .map(Number)
  .filter((y) => (TAX_CALENDARS[y] || []).length > 0)
  .sort((a, b) => a - b);

export function hasTaxData(year: number): boolean {
  return VERI_OLAN_YILLAR.includes(year);
}

/** İstenen yılın kayıtları; veri yoksa boş dizi (başka yılın verisi ASLA dönmez). */
export function getYearEvents(year: number): TaxEvent[] {
  return TAX_CALENDARS[year] || [];
}

/** Durum bilinmiyorsa 'tahmini' sayılır (yanlışlıkla "resmî" iddia etmemek için). */
export function getTaxDurum(year: number): TakvimDurumu {
  return (TAX_CALENDAR_DURUM as Record<number, TakvimDurumu>)[year] || "tahmini";
}

export function isTahmini(year: number): boolean {
  return getTaxDurum(year) === "tahmini";
}

/**
 * Sitenin "güncel" takvim yılı: bugünün yılının verisi varsa o yıl; yoksa
 * bugünden önceki en son veri yılı; o da yoksa ilk veri yılı.
 * Bugünün yılı ile farklıysa arayüz bunu kullanıcıya AÇIKÇA söylemeli.
 */
export function getSiteTaxYear(now: Date = new Date()): number {
  const y = now.getFullYear();
  if (hasTaxData(y)) return y;
  const onceki = VERI_OLAN_YILLAR.filter((v) => v < y);
  if (onceki.length) return onceki[onceki.length - 1];
  return VERI_OLAN_YILLAR[0] ?? y;
}

/**
 * Tanıtım metinleri ("2026 vergi takvimi", "443 vergi tarihi") için sayfa
 * yüklendiği andaki site yılı ve o yılın kayıt sayısı.
 */
export const SITE_TAX_YEAR: number = getSiteTaxYear();
export const SITE_TAX_COUNT: number = getYearEvents(SITE_TAX_YEAR).length;

/** Tahmini takvim uyarı metni. */
export function tahminiUyariMetni(year: number): string {
  return `Bu takvim tahminidir; GİB ${year} vergi takvimi yayımlandığında (beklenen 31 Aralık ${year - 1}) güncellenecektir. Resmî son günleri gib.gov.tr'den teyit edin.`;
}

/** "/vergi-tarihleri/ocak-2027" için slug: "ocak-2027". */
export function aySlug(monthSlug: string, year: number): string {
  return `${monthSlug}-${year}`;
}

/**
 * "ocak-2027" → { monthNum: 1, year: 2027 }. Yıl eki yoksa (eski "ocak" gibi)
 * `fallbackYear` kullanılır. Geçersiz ay → null.
 */
export function parseAySlug(ayslug: string | undefined, fallbackYear: number): { monthNum: number; year: number } | null {
  if (!ayslug) return null;
  const m = /^([a-z]+)(?:-(\d{4}))?$/.exec(ayslug);
  if (!m) return null;
  const month = MONTHS.find((x) => x.slug === m[1]);
  if (!month) return null;
  return { monthNum: month.num, year: m[2] ? parseInt(m[2], 10) : fallbackYear };
}

/** Bir yılın belirli ayındaki kayıtlar. */
export function getMonthEvents(year: number, monthNum: number): TaxEvent[] {
  return getYearEvents(year).filter((e) => parseInt(e.date.split("-")[0], 10) === monthNum);
}

/**
 * Ay ileri/geri gezinme; yıl sınırını geçer (Aralık 2026 → Ocak 2027) ama
 * verisi olmayan yıla geçişe izin vermez (null döner).
 */
export function shiftMonth(year: number, monthNum: number, delta: number): { year: number; monthNum: number } | null {
  const idx = year * 12 + (monthNum - 1) + delta;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  if (!hasTaxData(ny)) return null;
  return { year: ny, monthNum: nm };
}

/** Takvim widget'ının açılış durumu: bugünün yılı/ayı/günü (veri varsa). */
export function getWidgetDefault(now: Date = new Date()): { year: number; monthNum: number; day: number; bugununYiliVar: boolean } {
  const y = now.getFullYear();
  if (hasTaxData(y)) return { year: y, monthNum: now.getMonth() + 1, day: now.getDate(), bugununYiliVar: true };
  // Bugünün yılının verisi yok: en yakın veri yılını aç (geçmişse Aralık, gelecekse Ocak)
  const sy = getSiteTaxYear(now);
  return { year: sy, monthNum: sy < y ? 12 : 1, day: 1, bugununYiliVar: false };
}

/** Bugünden (dahil) itibaren yaklaşan kayıtlar; yıl sonunu geçip bir sonraki yılın verisine devam eder. */
export function getUpcomingEvents(now: Date = new Date(), limit = 5): { year: number; event: TaxEvent }[] {
  const y = now.getFullYear();
  const bugun = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const out: { year: number; event: TaxEvent }[] = [];
  for (const yil of [y, y + 1]) {
    const list = getYearEvents(yil)
      .filter((e) => yil > y || e.date >= bugun)
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date));
    for (const e of list) {
      out.push({ year: yil, event: e });
      if (out.length >= limit) return out;
    }
  }
  return out;
}
